import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Box, Grid, Tabs, Tab, Typography } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSTextField, RMSSelect, RMSCheckbox } from '../../../shared/components/form-controls';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import { VIN_REGEX } from '../../../shared/constants/regex';
import SectionLabel from './SectionLabel';
import VehicleTowingTab from './VehicleTowingTab';
import VehicleAttachmentsTab from './VehicleAttachmentsTab';
import {
  EDIT_VEHICLE_DEFAULT_VALUES,
  DICT_SELECT_PROPS,
  VEHICLE_TABS,
  VEHICLE_TAB_KEYS,
} from '../../constants/addVehicleFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { getVehicleDetails, updateVehicleDetails } from '../../services/userService';

const EditVehicleDialog = ({ open, onClose, ptsVehicleID, onSaved }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(VEHICLE_TAB_KEYS.EDIT);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [vehicle, setVehicle] = useState(null);

  const canSaveVehicle = getFullPermissions('globals', 'Vehicles', 'any').Edit;
  const isViewOnly = !canSaveVehicle;

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: EDIT_VEHICLE_DEFAULT_VALUES,
    disabled: isViewOnly,
  });

  const { control, setValue } = methods;

  const [Make, VIN, GarageIndicator] = useWatch({
    control,
    name: ['Make', 'VIN', 'GarageIndicator'],
  });

  const { isDirty } = useFormState({ control });
  const {
    vehicleMakeOptions,
    vehicleMakesModelsOptions,
    vehicleStyleOptions,
    vehicleColorOptions,
    vehicleClassificationOptions,
    vehicleBrandOptions,
    vehicleBranderOptions,
    vehiclePropertyOptions,
    vehicleCommercialOptions,
    transmissionOptions,
    garageFacilityOptions,
  } = useUserDictionaryOptions();
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const isVinInvalid = Boolean(VIN) && !VIN_REGEX.test(VIN);

  const modelOptions = useMemo(() => {
    if (!Make) return [];
    return vehicleMakesModelsOptions
      .filter((mm) => mm.MakeCode === Make)
      .map((mm) => ({ Code: mm.ModelCode, Description: mm.ModelDescription }));
  }, [Make, vehicleMakesModelsOptions]);

  const handleMakeChange = useCallback(() => setValue('Model', ''), [setValue]);

  const handleVinChange = useCallback((value) => setValue('VIN', (value || '').toUpperCase()), [
    setValue,
  ]);

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        Year: data.Year || null,
        Make: data.Make || null,
        Model: data.Model || null,
        Style: data.Style || null,
        DoorQuantity: data.DoorQuantity || null,
        PassengerSafeQty: data.PassengerSafeQty || null,
        SeatQty: data.SeatQty || null,
        PrimaryColor: data.PrimaryColor || null,
        SecondaryColor: data.SecondaryColor || null,
        InteriorColor: data.InteriorColor || null,
        Classification: data.Classification || null,
        Description: data.Description || null,
        Brand: data.Brand || null,
        Brander: data.Brander || null,
        VIN: data.VIN || null,
        VINAnalysisText: data.VINAnalysisText || null,
        PropertyID: data.PropertyID || null,
        Property: data.Property || null,
        CommercialID: data.CommercialID || null,
        Commercial: data.Commercial || null,
        EmissionInspectionQty: data.EmissionInspectionQty || null,
        Transmission: data.Transmission || null,
        IsCMVIndicator: data.IsCMVIndicator || false,
        IsRented: data.IsRented || false,
        IsWanted: data.IsWanted || false,
        GarageIndicator: data.GarageIndicator || false,
        GarageFacility: data.GarageFacility || null,
      };

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => updateVehicleDetails(ptsVehicleID, payload),
        onSuccess: () => {
          dispatch(notify('Vehicle updated successfully', 'success'));
          onSaved?.(payload);
          onClose?.();
        },
      });
    },
    [ptsVehicleID, dispatch, onSaved, onClose]
  );

  const handleSave = useMemo(() => methods.handleSubmit(onSubmit), [methods, onSubmit]);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this vehicle. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  useEffect(() => {
    if (!open || !ptsVehicleID) return;

    executeApiAction({
      setLoading,
      dispatch,
      action: () => getVehicleDetails(ptsVehicleID),
      onSuccess: (data) => {
        setVehicle(data || null);
        methods.reset({
          Year: data?.Year || '',
          Make: data?.Make || '',
          Model: data?.Model || '',
          Style: data?.Style || '',
          DoorQuantity: data?.DoorQuantity || '',
          PassengerSafeQty: data?.PassengerSafeQty || '',
          SeatQty: data?.SeatQty || '',
          PrimaryColor: data?.PrimaryColor || '',
          SecondaryColor: data?.SecondaryColor || '',
          InteriorColor: data?.InteriorColor || '',
          Classification: data?.Classification || '',
          Description: data?.Description || '',
          Brand: data?.Brand || '',
          Brander: data?.Brander || '',
          VIN: data?.VIN || '',
          VINAnalysisText: data?.VINAnalysisText || '',
          PropertyID: data?.PropertyID || '',
          Property: data?.Property || '',
          CommercialID: data?.CommercialID || '',
          Commercial: data?.Commercial || '',
          EmissionInspectionQty: data?.EmissionInspectionQty || '',
          Transmission: data?.Transmission || '',
          IsCMVIndicator: Boolean(data?.IsCMVIndicator),
          IsRented: Boolean(data?.IsRented),
          IsWanted: Boolean(data?.IsWanted),
          GarageIndicator: Boolean(data?.GarageIndicator),
          GarageFacility: data?.GarageFacility || '',
        });
      },
    });
  }, [open, ptsVehicleID, dispatch, methods]);

  return (
    <RMSCommonDialog
      open={open}
      title="Edit Vehicle"
      onClose={requestClose}
      onSave={handleSave}
      loading={loading || submitting}
      saveDisabled={submitting || !canSaveVehicle}
      maxWidth="lg"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: activeTab === VEHICLE_TAB_KEYS.EDIT,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        activeTab === VEHICLE_TAB_KEYS.EDIT && (
          <RMSRecordHistoryButton
            disabled={!vehicle?.Created}
            created={vehicle?.Created}
            createdBy={vehicle?.CreatedBy}
            updated={vehicle?.Updated}
            updatedBy={vehicle?.UpdatedBy}
          />
        )
      }>
      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        className="edit-person-dialog__tabs">
        {VEHICLE_TABS.map((tab) => (
          <Tab key={tab.key} value={tab.key} label={tab.label} />
        ))}
      </Tabs>

      {activeTab === VEHICLE_TAB_KEYS.TOWING && <VehicleTowingTab ptsVehicleID={ptsVehicleID} />}
      {activeTab === VEHICLE_TAB_KEYS.ATTACHMENTS && (
        <VehicleAttachmentsTab ptsVehicleID={ptsVehicleID} />
      )}

      {activeTab === VEHICLE_TAB_KEYS.EDIT && (
        <FormProvider {...methods}>
          <Box className="rms-dialog-section" mt={2}>
            <SectionLabel>Details</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <RMSTextField
                  name="Year"
                  label="Year"
                  type="number"
                  inputProps={{ min: 0, max: 2100 }}
                />
              </Grid>
              <Grid item xs={3}>
                <RMSSelect
                  name="Make"
                  label="Make"
                  options={vehicleMakeOptions}
                  onChange={handleMakeChange}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={3}>
                <RMSSelect
                  name="Model"
                  label="Model"
                  options={modelOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={3}>
                <RMSSelect
                  name="Style"
                  label="Style"
                  options={vehicleStyleOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={3}>
                <RMSTextField
                  name="DoorQuantity"
                  label="Doors"
                  type="number"
                  inputProps={{ min: 0, max: 20 }}
                />
              </Grid>
              <Grid item xs={3}>
                <RMSTextField
                  name="PassengerSafeQty"
                  label="Passenger Quantity"
                  type="number"
                  inputProps={{ min: 0, max: 1000 }}
                />
              </Grid>
              <Grid item xs={3}>
                <RMSTextField
                  name="SeatQty"
                  label="Seat Quantity"
                  type="number"
                  inputProps={{ min: 0, max: 1000 }}
                />
              </Grid>
              <Grid item xs={3}>
                <RMSSelect
                  name="Classification"
                  label="Classification"
                  options={vehicleClassificationOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSSelect
                  name="PrimaryColor"
                  label="Primary Color"
                  options={vehicleColorOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSSelect
                  name="SecondaryColor"
                  label="Secondary Color"
                  options={vehicleColorOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSSelect
                  name="InteriorColor"
                  label="Interior Color"
                  options={vehicleColorOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={6}>
                <RMSSelect
                  name="Brand"
                  label="Brand"
                  options={vehicleBrandOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={6}>
                <RMSSelect
                  name="Brander"
                  label="Brander"
                  options={vehicleBranderOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={12}>
                <RMSTextField
                  name="Description"
                  label="Description"
                  multiline
                  rows={2}
                  inputProps={{ maxLength: 8000 }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box className="rms-dialog-section">
            <SectionLabel>Credentials and Analysis</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <RMSTextField
                  name="VIN"
                  label="VIN"
                  inputProps={{ maxLength: 17 }}
                  onChange={handleVinChange}
                />
                {isVinInvalid && (
                  <Typography variant="caption" color="error">
                    VIN does not match the standard 17-character format
                  </Typography>
                )}
              </Grid>
              <Grid item xs={4}>
                <RMSTextField
                  name="VINAnalysisText"
                  label="VIN Analysis Text"
                  inputProps={{ maxLength: 2000 }}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSSelect
                  name="Transmission"
                  label="Transmission Code"
                  options={transmissionOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField
                  name="PropertyID"
                  label="Property ID"
                  inputProps={{ maxLength: 25 }}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSSelect
                  name="Property"
                  label="Property Code"
                  options={vehiclePropertyOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField
                  name="EmissionInspectionQty"
                  label="Emission Inspection"
                  type="number"
                />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField
                  name="CommercialID"
                  label="Commercial ID"
                  inputProps={{ maxLength: 25 }}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSSelect
                  name="Commercial"
                  label="Commercial Code"
                  options={vehicleCommercialOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSCheckbox name="IsCMVIndicator" label="CMV" />
              </Grid>
            </Grid>
          </Box>

          <Box className="rms-dialog-section rms-dialog-section--last">
            <SectionLabel>Flags</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <RMSCheckbox name="IsRented" label="Rented" />
              </Grid>
              <Grid item xs={3}>
                <RMSCheckbox name="IsWanted" label="Wanted" />
              </Grid>
              <Grid item xs={3}>
                <RMSCheckbox name="GarageIndicator" label="Garage Indicator" />
              </Grid>
              <Grid item xs={3}>
                <RMSSelect
                  name="GarageFacility"
                  label="Garage Facility"
                  options={garageFacilityOptions}
                  disabled={!GarageIndicator}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default EditVehicleDialog;
